/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../api/api';
import { getTokenData } from 'src/utils/api';
import { LOADING_STATUSES } from '../../../constants/statuses';
import { isResponseSuccess, formatFilters } from '../../../utils/api';
import { INITIAL_FILTERS_STATE } from '../constants/sourcePresets';
import { loadPresetCategories } from '../../presetCategories/features/presetCategoriesSlice';
import { CATEGORIES_FOR_PRESETS_TYPE } from '../../presetCategories/constants/presetCategories';

export const loadSourcePresets = createAsyncThunk(
  'sourcePresets/loadSourcePresets',
  async (data, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      const { filters, page, per } = getState().sourcePresetsReducer;
      const formattedFilters = formatFilters(filters);
      await dispatch(loadPresetCategories({ type: CATEGORIES_FOR_PRESETS_TYPE.source }));
      const response = await API(getTokenData()).SOURCE_PRESETS.load({
        ...formattedFilters,
        page: page + 1,
        per,
      });
      if (isResponseSuccess(response.status)) {
        return {
          data: response.data,
          totalItems: response.total,
        };
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const loadSourcePresetsById = createAsyncThunk(
  'sourcePresets/loadSourcePresetsById',
  async ({
    id,
  }, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(loadPresetCategories({ type: CATEGORIES_FOR_PRESETS_TYPE.source }));
      const response = await API(getTokenData()).SOURCE_PRESETS.loadById(id);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const loadAllSourcePresets = createAsyncThunk(
  'sourcePresets/loadAllSourcePresets',
  async (_, { getState, rejectWithValue }) => {
    const state = getState().sourcePresetsReducer;
    const { filters, per } = state;
    const formattedFilters = formatFilters(filters);
    const data = [];

    const load = async (page = 1) => {
      const response = await API(getTokenData()).SOURCE_PRESETS.load({
        ...formattedFilters,
        page,
        per,
      });

      if (!isResponseSuccess(response?.status) || !response?.data) {
        throw new Error('Failed to fetch data');
      }

      data.push(...response.data);

      const total = Number(response.total) || response.data.length;
      const totalPages = Math.ceil(total / per);

      return page < totalPages ? load(page + 1) : { data, totalItems: total };
    };

    try {
      return await load();
    } catch (error) {
      return rejectWithValue(error?.toString?.() || 'Unknown error');
    }
  },
);

export const createSourcePresets = createAsyncThunk(
  'sourcePresets/createPresetCategory',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).SOURCE_PRESETS.create(data);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const updateSourcePresets = createAsyncThunk(
  'sourcePresets/updateSourcePresets',
  async (data, {
    getState,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).SOURCE_PRESETS.update(data);
      if (isResponseSuccess(response.status)) {
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const deleteSourcePresets = createAsyncThunk(
  'sourcePresets/deleteSourcePresets',
  async (id, {
    getState,
    dispatch,
    rejectWithValue,
  }) => {
    try {
      const response = await API(getTokenData()).SOURCE_PRESETS.delete(id);
      if (isResponseSuccess(response.status)) {
        dispatch(loadSourcePresets());
        return response.data;
      }
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

const initialState = {
  entity: [],
  page: 0,
  per: 100,
  totalItems: 0,
  status: LOADING_STATUSES.IDLE,
  error: null,
  selected: {},
  selectedStatus: LOADING_STATUSES.IDLE,
  filters: INITIAL_FILTERS_STATE,
  allItems: [],
  allItemsStatus: LOADING_STATUSES.IDLE,
};

const sourcePresetsSlice = createSlice({
  name: 'sourcePresets',
  initialState,
  reducers: {
    setSelectedSourcePreset: (state, action) => {
      state.selected = action.payload;
    },
    setPageData: (state, action) => {
      state.page = action.payload.page;
      state.per = action.payload.per;
    },
    resetSourcePresets: () => initialState,
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSourcePresets.pending, (state) => {
        state.status = LOADING_STATUSES.LOADING;
      })
      .addCase(loadSourcePresets.fulfilled, (state, action) => {
        state.entity = action.payload?.data || [];
        state.totalItems = action.payload?.totalItems || 0;
        state.status = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadSourcePresets.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED;
      })
      .addCase(loadSourcePresetsById.pending, (state) => {
        state.selectedStatus = LOADING_STATUSES.LOADING;
      })
      .addCase(loadSourcePresetsById.fulfilled, (state, action) => {
        state.selected = action.payload || {};
        state.selectedStatus = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadSourcePresetsById.rejected, (state) => {
        state.selectedStatus = LOADING_STATUSES.FAILED;
      })
      .addCase(loadAllSourcePresets.pending, (state) => {
        state.allItems = [];
        state.allItemsStatus = LOADING_STATUSES.LOADING;
      })
      .addCase(loadAllSourcePresets.fulfilled, (state, action) => {
        state.allItems = action.payload?.data || [];
        state.allItemsStatus = LOADING_STATUSES.SUCCEEDED;
      })
      .addCase(loadAllSourcePresets.rejected, (state) => {
        state.allItems = [];
        state.allItemsStatus = LOADING_STATUSES.FAILED;
      });
  },
});

export const {
  setPageData,
  resetSourcePresets,
  setSelectedSourcePreset,
  setFilters,
} = sourcePresetsSlice.actions;
export default sourcePresetsSlice.reducer;

export const sourcePresetsChangePageAction = createAsyncThunk(
  'sourcePresets/sourcePresetsChangePageAction',
  async ({
    page = 0,
    per = 0,
  }, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setPageData({
        page,
        per,
      }));
      await dispatch(loadSourcePresets());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);

export const sourcePresetsFilterApply = createAsyncThunk(
  'sourcePresets/filterApply',
  async (newFilters, {
    dispatch,
    rejectWithValue,
  }) => {
    try {
      await dispatch(setFilters({
        ...newFilters,
      }));
      await dispatch(loadSourcePresets());
      return {};
    } catch (error) {
      return rejectWithValue(error.toString);
    }
  },
);
